/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import Box from 'fe-design-base/atoms/Box';
import IconButton from 'fe-design-base/molecules/IconButton';
import { compose } from 'redux';

import {
  getActiveTipPolicyId,
  getCurrentLocationName,
  getWebview,
} from 'selectors/session';

import {
  fetchProductTipManager as getFetchProductTipManager,
  fetchTipManagerSubscriptions as getFetchTipManagerSubscriptions,
} from 'features/managePlan/actions';
import WebViewAlt from 'features/managePlan/ManagePlanView/WebViewAlt';
import { selectProductTipManagerPrice } from 'features/managePlan/selectors';
import { updateTipPolicyEffectiveDate as updatePolicyEffectiveDate } from 'features/monetization/TipManagerCheckoutDrawer/thunk';
import { TipManagerCheckoutDrawerProps } from 'features/monetization/TipManagerCheckoutDrawer/types';
import {
  getCurrentPayrollPeriod,
  getPreviousPayrollPeriod,
} from 'features/settings/TipManagerSettingView/selectors';
import { fetchLocationTipPolicies as getFetchLocationTipPolicies } from 'features/settings/TipManagerSettingView/thunks';

import Drawer from 'components/Drawer';

import { cxHelpers } from 'util/className';
import { toI18n } from 'util/i18n';
import {
  EVENT_ACTIONS,
  EVENT_CATEGORIES,
  PRODUCT_AREAS,
  TRACK_ACTION_TYPES,
} from 'util/tracking_constants';
import { useTrackUx, withUxRoot } from 'util/uxEvents';

import { selectInitialViewKey } from './selectors';
import { closeTipManagerCheckoutDrawer as getCloseTipManagerCheckoutDrawer } from './slice';
import * as views from './views';

const { cx } = cxHelpers('TipManagerCheckoutDrawer');

export const TipManagerCheckoutDrawer: React.FC<
  TipManagerCheckoutDrawerProps
> = ({
  closeTipManagerCheckoutDrawer,
  initialViewKey,
  fetchLocationTipPolicies,
  currentPayrollPeriod,
  currentLocationName,
  fetchProductTipManager,
  fetchTipManagerSubscriptions,
  price,
  subscriptionType,
  updateTipPolicyEffectiveDate,
  previousPayrollPeriod,
  tipPolicyId,
  isWebView,
}) => {
  const trackUxEvent = useTrackUx();

  const [currentViewKey, setCurrentViewKey] = useState(initialViewKey);
  const [selectedEffectiveDate, setSelectedEffectiveDate] = useState('current');
  const [isLoading, setIsLoading] = useState(false);

  const CurrentView = views[currentViewKey as keyof typeof views];
  const currentPayPeriodStartDate = currentPayrollPeriod?.[0] || '';
  const previousPayPeriodStartDate = previousPayrollPeriod?.[0] || '';

  // Get Purchase Abandoned event data for Tip Manager checkout
  const getPurchaseAbandonedEventData = useCallback(
    (closingWindow: boolean) => ({
      selected_plan: null, // No plan selection in add-on only checkout
      selected_billing_frequency: null, // No billing frequency for add-ons
      selected_add_on: ['tip_manager'],
      promo_code: null, // No promo code support in current implementation
      closing_window: closingWindow,
    }),
    []
  );

  const handleTracking = useCallback(() => {
    if (currentViewKey === 'Success') {
      trackUxEvent(EVENT_ACTIONS.BUTTON_CLICKED, TRACK_ACTION_TYPES.CLICK, {
        element: 'close_button',
        stage: 'checkout_completed',
      });
    } else {
      trackUxEvent(
        'Purchase Abandoned',
        TRACK_ACTION_TYPES.CLICK,
        getPurchaseAbandonedEventData(false)
      );
    }
  }, [currentViewKey, trackUxEvent, getPurchaseAbandonedEventData]);

  // Enhanced close function with Purchase Abandoned tracking
  const handleCloseTipManagerCheckoutDrawer = useCallback(() => {
    handleTracking();

    closeTipManagerCheckoutDrawer();
  }, [closeTipManagerCheckoutDrawer, handleTracking]);

  const handleOnLoading = useCallback((loading: boolean) => {
    setIsLoading(loading);
  }, []);

  const handleOnSuccess = useCallback(() => {
    setCurrentViewKey('Success');
    fetchLocationTipPolicies();
    fetchTipManagerSubscriptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchLocationTipPolicies();
    fetchProductTipManager();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Add beforeunload event listener to detect window/tab closing
  useEffect(() => {
    const handleBeforeUnload = () => {
      handleTracking();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [handleTracking]);

  const updateTipPolicy = useCallback(() => {
    if (selectedEffectiveDate === 'current') return Promise.resolve();

    return updateTipPolicyEffectiveDate({
      id: tipPolicyId,
      effective_date: previousPayPeriodStartDate,
    });
  }, [
    previousPayPeriodStartDate,
    selectedEffectiveDate,
    tipPolicyId,
    updateTipPolicyEffectiveDate,
  ]);

  if (isWebView) {
    return (
      <Drawer
        className={cx()}
        maxWidth="464px"
        open
        variant="temporary"
        onClose={handleCloseTipManagerCheckoutDrawer}
      >
        <Box hright mt={16} mr={16}>
          <IconButton
            onClick={handleCloseTipManagerCheckoutDrawer}
            icon="Close"
            size="small"
            uxElement="checkout_drawer.close_button"
            label={toI18n('checkout_drawer.close_button_labels.tip_manager')}
          />
        </Box>
        <WebViewAlt />
      </Drawer>
    );
  }

  return (
    <Drawer
      className={cx()}
      maxWidth="464px"
      open
      variant="temporary"
      onClose={handleCloseTipManagerCheckoutDrawer}
    >
      <Box hright mt={16} mr={16}>
        <IconButton
          onClick={handleCloseTipManagerCheckoutDrawer}
          icon="Close"
          size="small"
          uxElement="checkout_drawer.close_button"
          label={toI18n('checkout_drawer.close_button_labels.tip_manager')}
        />
      </Box>
      <Box mt={12} ph={32}>
        <CurrentView
          onSuccess={handleOnSuccess}
          onLoading={handleOnLoading}
          isLoading={isLoading}
          currentPayPeriodStartDate={currentPayPeriodStartDate}
          previousPayPeriodStartDate={previousPayPeriodStartDate}
          closeDrawer={handleCloseTipManagerCheckoutDrawer}
          locationName={currentLocationName}
          price={price}
          subscriptionType={subscriptionType}
          updateTipPolicy={updateTipPolicy}
          setSelectedEffectiveDate={setSelectedEffectiveDate}
        />
      </Box>
    </Drawer>
  );
};
export default compose(
  withUxRoot({
    productArea: PRODUCT_AREAS.TIP_MANAGER,
    eventCategory: EVENT_CATEGORIES.CHECKOUT,
  }),
  connect(
    state => ({
      initialViewKey: selectInitialViewKey(state),
      currentPayrollPeriod: getCurrentPayrollPeriod(state),
      previousPayrollPeriod: getPreviousPayrollPeriod(state),
      currentLocationName: getCurrentLocationName(state),
      price: selectProductTipManagerPrice(state),
      subscriptionType: 'tip_manager',
      tipPolicyId: getActiveTipPolicyId(state),
      isWebView: getWebview(state),
    }),
    {
      closeTipManagerCheckoutDrawer: getCloseTipManagerCheckoutDrawer,
      fetchLocationTipPolicies: getFetchLocationTipPolicies,
      fetchProductTipManager: getFetchProductTipManager,
      fetchTipManagerSubscriptions: getFetchTipManagerSubscriptions,
      updateTipPolicyEffectiveDate: updatePolicyEffectiveDate,
    }
  )
)(TipManagerCheckoutDrawer);
